/**
 * `/admin/compliance-records` — admin triage list across the M13
 * dashboard filter views (M13 drill-down slice; closes the deferred
 * "drill-down links from dashboard cards" bullet).
 *
 * Consumes `GET /compliance-records` (`ApiClient.adminListComplianceRecords`).
 * Each snapshot card on `/admin` hands off to this page with `?view=…`
 * preselected (PRD 06 §Dashboard — "Each row … links to the relevant
 * person/record detail page").
 *
 * Filter tabs map 1:1 to the stable `view` enum:
 *   - `all_active`      → no server filter
 *   - `new_this_week`
 *   - `awaiting_verification`
 *   - `expiring_in_30`
 *   - `expired_overdue`
 *
 * Changing the filter or the page always resets to page 1 so the list
 * state is always coherent with the query. 401 short-circuits to
 * `/admin/sign-in`; non-401 errors surface an inline alert + Retry.
 *
 * Row click navigates to `/admin/compliance-records/:recordId` — the
 * verification workflow screen already shipped in M13. A row's person
 * link is a dedicated affordance that navigates to `/admin/people/:personId`
 * so admins who need full context on the volunteer (not just the record)
 * can drill sideways without losing the triage list in history.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type {
  ComplianceRecordListRow,
  ComplianceRecordStatus,
  ComplianceRecordsListQuery,
  ComplianceRecordsListResponse,
  PersonStatus,
} from '@isms/shared';

export interface ComplianceRecordsListPageProps {
  client: ApiClient;
  signInPath?: string;
}

type ViewFilter = 'all_active' | NonNullable<ComplianceRecordsListQuery['view']>;

interface FilterTab {
  value: ViewFilter;
  label: string;
  hint: string;
}

const FILTER_TABS: FilterTab[] = [
  {
    value: 'all_active',
    label: 'All',
    hint: 'Every compliance record across the register',
  },
  {
    value: 'new_this_week',
    label: 'New this week',
    hint: 'Records created in the last seven days',
  },
  {
    value: 'awaiting_verification',
    label: 'Awaiting verification',
    hint: 'Submitted records waiting for admin verification',
  },
  {
    value: 'expiring_in_30',
    label: 'Expiring in 30 days',
    hint: 'Verified records whose expiry lands inside 30 days',
  },
  {
    value: 'expired_overdue',
    label: 'Expired and overdue',
    hint: 'Records past their expiry date',
  },
];

const RECORD_STATUS_LABELS: Record<ComplianceRecordStatus, string> = {
  pending: 'Pending',
  submitted: 'Awaiting verification',
  verified: 'Verified',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  superseded: 'Superseded',
  archived: 'Archived',
};

const PERSON_STATUS_LABELS: Record<PersonStatus, string> = {
  active: 'Active',
  onboarding: 'Onboarding',
  inactive: 'Inactive',
  stood_down: 'Stood down',
};

const PAGE_SIZE = 25;

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: ComplianceRecordsListResponse };

function parseView(raw: string | null): ViewFilter {
  if (!raw) return 'all_active';
  const match = FILTER_TABS.find((t) => t.value === raw);
  return match ? match.value : 'all_active';
}

export function ComplianceRecordsListPage({
  client,
  signInPath = '/admin/sign-in',
}: ComplianceRecordsListPageProps): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewFilter = parseView(searchParams.get('view'));
  const [page, setPage] = useState(1);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await client.adminListComplianceRecords({
        page,
        page_size: PAGE_SIZE,
        ...(viewFilter !== 'all_active' ? { view: viewFilter } : {}),
      });
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the compliance records list. Please check your connection and try again.",
      });
    }
  }, [client, viewFilter, page, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleFilterChange(value: ViewFilter): void {
    setPage(1);
    if (value === 'all_active') {
      setSearchParams({});
    } else {
      setSearchParams({ view: value });
    }
  }

  function handleRowClick(recordId: string): void {
    navigate(`/admin/compliance-records/${encodeURIComponent(recordId)}`);
  }

  const activeTab = FILTER_TABS.find((t) => t.value === viewFilter)!;

  return (
    <main className="admin-records" aria-labelledby="admin-records-heading">
      <nav className="admin-records-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-records-header">
        <h1 id="admin-records-heading">Compliance records</h1>
        <p className="admin-records-subtitle">{activeTab.hint}.</p>
      </header>

      <div className="admin-records-toolbar">
        <div
          className="admin-records-filter-tabs"
          role="tablist"
          aria-label="Filter compliance records"
        >
          {FILTER_TABS.map((tab) => {
            const isActive = tab.value === viewFilter;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`admin-records-filter-tab ${isActive ? 'is-active' : ''}`}
                onClick={() => handleFilterChange(tab.value)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <ComplianceRecordsListBody
        state={state}
        onRetry={() => void load()}
        onRowClick={handleRowClick}
        onPageChange={(next) => setPage(next)}
      />
    </main>
  );
}

interface ComplianceRecordsListBodyProps {
  state: LoadState;
  onRetry: () => void;
  onRowClick: (recordId: string) => void;
  onPageChange: (page: number) => void;
}

function ComplianceRecordsListBody({
  state,
  onRetry,
  onRowClick,
  onPageChange,
}: ComplianceRecordsListBodyProps): JSX.Element {
  if (state.kind === 'loading') {
    return (
      <section className="admin-records-body" aria-busy="true">
        <p className="admin-records-loading">Loading compliance records…</p>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="admin-records-body">
        <div className="admin-records-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const { items, pagination } = state.data;

  if (items.length === 0) {
    return (
      <section className="admin-records-body">
        <p className="admin-records-empty">
          No compliance records match this filter. Nothing for admins to action here.
        </p>
      </section>
    );
  }

  return (
    <section className="admin-records-body">
      <p className="admin-records-summary">
        Showing {items.length} of {pagination.total_items}{' '}
        {pagination.total_items === 1 ? 'record' : 'records'}
      </p>

      <ul className="admin-records-list">
        {items.map((row) => (
          <RecordRow key={toIdString(row.record_id)} row={row} onClick={onRowClick} />
        ))}
      </ul>

      <PaginationControls pagination={pagination} onPageChange={onPageChange} />
    </section>
  );
}

interface RecordRowProps {
  row: ComplianceRecordListRow;
  onClick: (recordId: string) => void;
}

function RecordRow({ row, onClick }: RecordRowProps): JSX.Element {
  const recordId = toIdString(row.record_id);
  const personId = toIdString(row.person.person_id);
  const personLabel = row.person.preferred_name
    ? `${row.person.full_name} (${row.person.preferred_name})`
    : row.person.full_name;
  const expiryHint = buildExpiryHint(row);

  return (
    <li className="admin-records-row">
      <button
        type="button"
        className="admin-records-row-button"
        onClick={() => onClick(recordId)}
        aria-label={`Open compliance record for ${personLabel} — ${row.requirement.name}`}
      >
        <span className="admin-records-row-line admin-records-row-line-primary">
          <span className="admin-records-row-person">{personLabel}</span>
          <span className="admin-records-row-item-type">{row.requirement.name}</span>
        </span>
        <span className="admin-records-row-line admin-records-row-line-meta">
          <RecordStatusBadge status={row.status} />
          <span className="admin-records-row-short-code">{row.requirement.short_code}</span>
          {expiryHint ? <span className="admin-records-row-expiry">{expiryHint}</span> : null}
          <span className="admin-records-row-person-status">
            Volunteer: {PERSON_STATUS_LABELS[row.person.status]}
          </span>
        </span>
      </button>
      <div className="admin-records-row-side">
        <Link
          to={`/admin/people/${encodeURIComponent(personId)}`}
          className="admin-records-row-person-link"
        >
          View volunteer →
        </Link>
      </div>
    </li>
  );
}

function buildExpiryHint(row: ComplianceRecordListRow): string | null {
  if (!row.expiry_date) return null;
  const expiry = new Date(row.expiry_date + 'T00:00:00Z').getTime();
  const today = (() => {
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  })();
  const msPerDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((expiry - today) / msPerDay);
  if (diffDays < 0) {
    const overdue = Math.abs(diffDays);
    return `Overdue by ${overdue} ${overdue === 1 ? 'day' : 'days'}`;
  }
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  return `Expires in ${diffDays} days`;
}

interface RecordStatusBadgeProps {
  status: ComplianceRecordStatus;
}

function RecordStatusBadge({ status }: RecordStatusBadgeProps): JSX.Element {
  return (
    <span className={`admin-records-status admin-records-status-${status}`}>
      {RECORD_STATUS_LABELS[status]}
    </span>
  );
}

interface PaginationControlsProps {
  pagination: ComplianceRecordsListResponse['pagination'];
  onPageChange: (page: number) => void;
}

function PaginationControls({
  pagination,
  onPageChange,
}: PaginationControlsProps): JSX.Element | null {
  if (pagination.total_pages <= 1) {
    return null;
  }
  const prevDisabled = pagination.page <= 1;
  const nextDisabled = pagination.page >= pagination.total_pages;
  return (
    <nav className="admin-records-pagination" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(pagination.page - 1)}
        disabled={prevDisabled}
      >
        Previous
      </button>
      <span className="admin-records-pagination-label" aria-live="polite">
        Page {pagination.page} of {pagination.total_pages}
      </span>
      <button
        type="button"
        onClick={() => onPageChange(pagination.page + 1)}
        disabled={nextDisabled}
      >
        Next
      </button>
    </nav>
  );
}

function toIdString(id: ComplianceRecordListRow['record_id']): string {
  return typeof id === 'string' ? id : String(id);
}
